import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { FlexProps, JustifyContentProps, OverflowProps, SpaceProps } from 'styled-system';
import { flex, justifyContent, overflow, space, themeGet } from 'styled-system';

import { getForwardPropOpts } from 'common/theme/helpers/forward-props';
import { focusOutlineCSSWithOffset } from 'common/theme/styles';
import { textOverflow, type TextOverflowProps } from 'common/theme/system-utilities';
import { ButtonBase } from '../Button';
import { BodyBoldBB2, BodyRegularBR2 } from '../Typography';

export interface BaseTabButtonProps extends SpaceProps {
  isActive: boolean;
}

const activeButtonCSS = (props: BaseTabButtonProps) =>
  props.isActive &&
  css`
    background: ${themeGet('colors.sys.neutral.background.active')(props)};
    color: ${themeGet('colors.sys.neutral.text.strong')(props)};

    &:hover {
      background: ${themeGet('colors.sys.neutral.background.active')(props)};
    }
  `;

export const BaseTabButton = styled(ButtonBase, getForwardPropOpts(['isActive']))<
  BaseTabButtonProps & JustifyContentProps
>`
  ${space};
  ${justifyContent};
  border-radius: ${themeGet('radii.x-large')};
  white-space: nowrap;
  color: ${themeGet('colors.sys.neutral.text.default')};
  ${focusOutlineCSSWithOffset};

  &:hover {
    background: ${themeGet('colors.sys.neutral.background.hover')};
    color: ${themeGet('colors.sys.neutral.text.hover')};
  }

  ${activeButtonCSS};
`;

const StyledTabButton = styled(BaseTabButton)<
  BaseTabButtonProps & FlexProps & OverflowProps & TextOverflowProps
>`
  ${flex};
  ${overflow};
  ${textOverflow};
`;

export interface TabItemProps {
  id: string;
  label: string;
  isActive: boolean;
  index: number;
  count: number;
  tabIndex: number;
  onClick: () => void;
  buttonRef: (el: Nullable<HTMLButtonElement>) => void;
}

export const TabItem = ({
  id,
  label,
  isActive,
  index,
  count,
  tabIndex,
  onClick,
  buttonRef,
}: TabItemProps) => {
  const TypoComponent = isActive ? BodyBoldBB2 : BodyRegularBR2;

  return (
    <StyledTabButton
      ref={buttonRef}
      id={`tab-${id}`}
      role="tab"
      aria-selected={isActive}
      aria-setsize={count}
      aria-posinset={index + 1}
      isActive={isActive}
      tabIndex={tabIndex}
      px="sp_8"
      onClick={onClick}
      type="button"
      flex="1"
      justifyContent="center"
      overflow="hidden"
      textOverflow="ellipsis"
    >
      <TypoComponent
        color={isActive ? 'sys.neutral.text.active' : 'sys.neutral.text.default'}
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {label}
      </TypoComponent>
    </StyledTabButton>
  );
};

TabItem.displayName = 'TabItem';
